import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { CheckCircle, Shield, Car, Bike, Palette, Sparkles, Sun, Wrench, HelpCircle } from "lucide-react";
import heroPPF from "@/assets/hero-ppf.jpg";
import ferrariFullBody from "@/assets/ferrari-sf90-full-body.jpg";
import ferrariTrackPack from "@/assets/ferrari-sf90-track-pack.jpg";
import ferrariFullFront from "@/assets/ferrari-sf90-full-front.jpg";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PaintProtectionFilm = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Paint Protection Film Las Vegas (Clear Bra) | XPEL Official</title>
        <meta name="description" content="Protect your vehicle with XPEL Ultimate Plus PPF in Las Vegas. Self-healing clear bra technology guards against rock chips, scratches & UV damage. Free quote." />
        <link rel="canonical" href="https://battlebornppf.com/services/paint-protection-film" />
        <link rel="preload" as="image" href={heroPPF} />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroPPF} 
            alt="XPEL Ultimate Plus paint protection film being applied to luxury vehicle at Battle Born PPF Las Vegas" 
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/85 to-black/75" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <Shield className="w-16 h-16 text-accent mb-6" />
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Paint Protection Film
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              XPEL Ultimate Plus self-healing film technology - The ultimate defense for your vehicle's finish
            </p>
            <Button asChild size="lg" variant="premium">
              <Link to="/contact">Request Free Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center">
              Why Paint Protection Film?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Self-Healing Technology</h3>
                  <p className="text-muted-foreground">
                    Minor scratches and swirl marks disappear with heat, keeping your vehicle looking pristine
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Rock Chip Protection</h3>
                  <p className="text-muted-foreground">
                    8-mil thick film absorbs impact from road debris, preventing paint damage
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">UV & Chemical Resistant</h3>
                  <p className="text-muted-foreground">
                    Protects against sun damage, acid rain, bird droppings, and environmental contaminants
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Maintains Resale Value</h3>
                  <p className="text-muted-foreground">
                    Preserves factory paint condition, maximizing your vehicle's long-term value
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Invisible Protection</h3>
                  <p className="text-muted-foreground">
                    Crystal-clear finish maintains your vehicle's original appearance
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">10-Year Warranty</h3>
                  <p className="text-muted-foreground">
                    Manufacturer-backed warranty for peace of mind and long-term protection
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Paint Protection Film? */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-8 text-center">
              What is Paint Protection Film?
            </h2>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
              <p>
                Paint Protection Film (PPF), also known as clear bra, is an optically clear, thermoplastic urethane film applied to your vehicle's painted surfaces. Acting as an invisible armor, PPF absorbs impacts from rock chips, road debris, sand, and gravel that would otherwise chip, scratch, or permanently damage your factory paint. The film is engineered to be virtually undetectable once installed, preserving the depth, gloss, and color of your original finish.
              </p>
              <p>
                What sets modern PPF apart is its self-healing top coat. Minor scratches, swirl marks, and surface abrasions disappear on their own when exposed to heat — whether from the Las Vegas sun, warm water, or even the warmth of your hand. This means your vehicle continuously restores itself to a like-new appearance without any additional effort or cost. The film also provides robust UV protection, preventing paint oxidation and fading that's accelerated by Nevada's intense desert sun.
              </p>
              <p>
                Beyond physical protection, PPF guards against chemical stains from bug splatter, bird droppings, tree sap, and mineral deposits from hard water. These contaminants can etch into unprotected clear coat within hours, especially in high temperatures. With PPF installed, they sit on the film's surface and wash away cleanly, leaving your paint untouched underneath.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose XPEL PPF? */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-8 text-center">
              Why Choose XPEL PPF?
            </h2>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed mb-12">
              <p>
                At Battle Born Protection Films, we exclusively install XPEL products — the industry's most trusted name in paint protection. As certified XPEL installers, we have access to their complete product line and precision-cut DAP (Design Access Program) patterns for thousands of vehicle models, ensuring every installation fits your car with factory-level precision.
              </p>
              <p>
                <strong className="text-foreground">XPEL Ultimate Plus</strong> is the flagship clear PPF, offering 8 mils of impact-absorbing protection with an advanced self-healing top coat, superior optical clarity, and stain resistance. It's backed by XPEL's industry-leading <strong className="text-foreground">10-year warranty</strong> against yellowing, cracking, peeling, staining, and delamination. Ultimate Plus is the gold standard for owners who demand invisible, long-lasting protection.
              </p>
              <p>
                <strong className="text-foreground">XPEL Stealth</strong> transforms any glossy paint into a stunning satin/matte finish while delivering the same level of protection as Ultimate Plus. It's the perfect choice for owners who want a distinctive, head-turning matte look that's completely reversible — your original gloss paint remains pristine underneath, ready to be revealed if you ever remove the film.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Installation Process */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Wrench className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Our Installation Process
              </h2>
              <p className="text-xl text-muted-foreground">
                Precision craftsmanship from consultation to completion
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { step: "01", title: "Consultation", desc: "We assess your vehicle, discuss your driving habits, and recommend the ideal coverage package for your needs and budget." },
                { step: "02", title: "Custom Template Cutting", desc: "Using XPEL's proprietary DAP software, we precision-cut film patterns specific to your exact year, make, and model for a seamless fit." },
                { step: "03", title: "Surface Preparation", desc: "Your vehicle is thoroughly washed, clay-barred, and decontaminated. Any paint imperfections are corrected before film application." },
                { step: "04", title: "Film Installation", desc: "Our certified technicians apply each panel in our climate-controlled facility, ensuring bubble-free adhesion and invisible edges." },
                { step: "05", title: "Quality Inspection", desc: "Every inch of applied film is inspected under specialized lighting to ensure flawless coverage and proper edge adhesion." },
                { step: "06", title: "Curing & Delivery", desc: "The film cures for 24–48 hours. We walk you through aftercare instructions so you can maintain your protection for years to come." },
              ].map((item) => (
                <Card key={item.step} className="bg-card border-border">
                  <CardContent className="p-6">
                    <span className="text-4xl font-heading font-bold text-accent/30">{item.step}</span>
                    <h3 className="text-xl font-heading font-bold mt-2 mb-3">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              See XPEL PPF In Action
            </h2>
            <p className="text-center text-muted-foreground mb-8">
              Watch how our paint protection film defends against rock chips, scratches, and more
            </p>
            <div className="relative aspect-video rounded-lg overflow-hidden border border-border">
              <video
                className="w-full h-full object-cover"
                controls
                autoPlay
                muted
                loop
                playsInline
              >
                <source 
                  src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/videos/1763209886542.mp4`}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* PPF Packages Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* PPF Packages Visual Comparison */}
            <div className="bg-[#1B1A1A] -mx-4 px-4 py-16 rounded-2xl">
              <h3 className="text-3xl font-heading font-bold mb-12 text-center text-white">
                Our Paint Protection Film Packages
              </h3>
              
              <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {/* Full Body Protection */}
                <Card className="bg-[#2A2A2A] border-white/10 rounded-2xl overflow-hidden">
                  <div className="relative h-64 overflow-hidden bg-black rounded-t-2xl">
                    <img 
                      src={ferrariFullBody}
                      alt="Full body PPF coverage diagram showing all painted panels protected on Ferrari SF90"
                      className="w-full h-full object-contain"
                      width="600"
                      height="400"
                      loading="lazy"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-2xl font-heading font-bold text-white mb-4">
                      Full Body Protection
                    </h4>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Hood, Roof, and Trunk</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Fenders and Quarters</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Front and Rear Bumper</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Doors and Rocker Panels</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Mirror Caps & Pillars</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Headlights</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 italic text-center mt-6">
                      Covers all painted areas of your vehicle
                    </p>
                  </CardContent>
                </Card>

                {/* Track Pack Coverage */}
                <Card className="bg-[#2A2A2A] border-white/10 rounded-2xl overflow-hidden">
                  <div className="relative h-64 overflow-hidden bg-black rounded-t-2xl">
                    <img 
                      src={ferrariTrackPack}
                      alt="Track pack PPF coverage diagram showing hood fenders bumper and rocker panels on Ferrari SF90"
                      className="w-full h-full object-contain"
                      width="600"
                      height="400"
                      loading="lazy"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-2xl font-heading font-bold text-white mb-4">
                      Track Pack Coverage
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Hood</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Fenders</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Front Bumper</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Mirror Caps</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Rocker Panels</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">A Pillar and Roofstrip</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Headlights</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Full Front Protection */}
                <Card className="bg-[#2A2A2A] border-white/10 rounded-2xl overflow-hidden">
                  <div className="relative h-64 overflow-hidden bg-black rounded-t-2xl">
                    <img 
                      src={ferrariFullFront}
                      alt="Full front PPF coverage diagram showing hood fenders bumper and mirrors on Ferrari SF90"
                      className="w-full h-full object-contain"
                      width="600"
                      height="400"
                      loading="lazy"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-2xl font-heading font-bold text-white mb-4">
                      Full Front Protection
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Hood</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Fenders</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Front Bumper</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Mirror Caps</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Headlights</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Motorcycle PPF Section */}
      <section className="py-20 bg-darker-bg border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Bike className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Motorcycle Paint Protection Film
              </h2>
              <p className="text-xl text-muted-foreground">
                Premium protection specifically designed for motorcycles, sport bikes, and touring bikes
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-accent" />
                    Common Coverage Areas
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Gas Tank (Full Coverage)</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Front Fender & Side Fairings</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Swingarm & Frame Guards</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Tail Section & Rear Fender</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Custom High-Impact Zones</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-accent" />
                    Motorcycle-Specific Benefits
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Protection from chain lube splatter</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Guards against boot rub and scratches</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Rock chip defense for exposed surfaces</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Maintains resale value of rare bikes</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Custom-cut patterns for any model</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <p className="text-muted-foreground mb-6">
                Custom PPF solutions for sport bikes, cruisers, adventure bikes, and touring motorcycles
              </p>
              <Button asChild variant="premium" size="lg">
                <Link to="/contact">Request Motorcycle Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Customization Options Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Palette className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                PPF Customization Options
              </h2>
              <p className="text-xl text-muted-foreground">
                Transform your vehicle's appearance while protecting the paint
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Stealth/Matte PPF */}
              <div>
                <Card className="bg-card border-border h-full">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                        <Sparkles className="w-8 h-8 text-accent" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-heading font-bold">XPEL Stealth (Matte) PPF</h3>
                        <p className="text-sm text-accent">Gloss to Satin Finish Conversion</p>
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-6">
                      Transform your glossy paint into a stunning satin/matte finish while protecting it. XPEL Stealth film provides the same self-healing protection as Ultimate Plus, with a unique matte appearance.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Reversible matte finish (no paint modification)</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Self-healing matte surface technology</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Available on any package (partial to full body)</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Distinctive luxury appearance</span>
                      </div>
                    </div>

                    {/* XPEL Stealth Video */}
                    <div className="relative aspect-video rounded-lg overflow-hidden border border-border mb-4">
                      <video
                        className="w-full h-full object-cover"
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                      >
                        <source src="/videos/xpel-stealth-demo.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Colored PPF */}
              <div>
                <Card className="bg-card border-border h-full">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                        <Palette className="w-8 h-8 text-accent" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-heading font-bold">XPEL Colored PPF</h3>
                        <p className="text-sm text-accent">Custom Color + Protection</p>
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-6">
                      Change your vehicle's color while adding protection. XPEL offers a range of vibrant, high-quality colored PPF options that combine paint protection with a complete color transformation.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Full color change without permanent paint</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Self-healing colored film technology</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Wide range of colors and finishes</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Protects original factory paint underneath</span>
                      </div>
                    </div>

                    {/* XPEL Colored PPF Video */}
                    <div className="relative aspect-video rounded-lg overflow-hidden border border-border mb-4">
                      <iframe
                        src="https://www.youtube.com/embed/Ssw4bl9AqK8"
                        title="XPEL Colored PPF"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-muted-foreground mb-6">
                Interested in Stealth or Colored PPF? Contact us to see samples and discuss your vision.
              </p>
              <Button asChild variant="premium" size="lg">
                <Link to="/contact">Explore Customization Options</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Las Vegas Climate Benefits */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Sun className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Why PPF is Essential in Las Vegas
              </h2>
              <p className="text-xl text-muted-foreground">
                The desert environment demands serious paint protection
              </p>
            </div>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
              <p>
                Las Vegas presents one of the harshest environments for automotive paint in the country. With over 300 days of sunshine annually and summer temperatures regularly exceeding 115°F, UV radiation breaks down clear coat at an accelerated rate. Without protection, you'll notice paint fading, oxidation, and chalking far sooner than in milder climates. PPF blocks harmful UV rays, keeping your paint vibrant and protected year-round.
              </p>
              <p>
                Highway driving on I-15, US-95, and the 215 Beltway exposes your vehicle to constant sand, gravel, and road debris kicked up by traffic. Construction zones — a near-permanent fixture of Las Vegas roads — amplify this risk with loose aggregate and debris fields. The front end of your vehicle takes the brunt of this punishment, making PPF on the hood, bumper, and fenders a critical investment for any daily driver or commuter.
              </p>
              <p>
                The extreme heat also accelerates damage from organic contaminants. Bug splatter, bird droppings, and tree sap that might sit harmlessly on paint in cooler climates can etch into unprotected clear coat within hours in the Nevada sun. PPF provides a sacrificial barrier that prevents these contaminants from ever reaching your paint, saving you from expensive paint correction or refinishing down the road.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PPF FAQ Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <HelpCircle className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about paint protection film
              </p>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="how-long" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  How long does PPF last?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  XPEL Ultimate Plus PPF is engineered to last 10 years or more with proper care. It comes backed by XPEL's 10-year manufacturer warranty against yellowing, cracking, peeling, and delamination. In practice, many of our Las Vegas clients see their film performing flawlessly well beyond the warranty period, especially with regular washing and occasional professional maintenance.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="removal" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  Can PPF be removed?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  Yes, PPF is fully removable without damaging your factory paint. When professionally removed, the film peels away cleanly, revealing the original paint in the same condition as the day the film was applied. This makes PPF an excellent option for leased vehicles or owners who want to update their protection after several years.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="maintenance" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  How do you maintain PPF?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  PPF is low-maintenance. Wash your vehicle regularly with a pH-neutral car wash soap and avoid abrasive compounds or automatic car washes with stiff brushes. You can apply ceramic coating or spray sealant on top of PPF for added hydrophobic properties. We recommend avoiding wax products that contain petroleum distillates, as they can cloud the film over time.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="yellowing" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  Does PPF yellow over time?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  Older-generation PPF products were prone to yellowing, but XPEL Ultimate Plus uses advanced non-yellowing adhesive and top coat technology specifically designed to resist discoloration. The 10-year warranty explicitly covers yellowing, so you're protected if any discoloration occurs. This is one of the key reasons we exclusively use XPEL products.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="install-time" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  How long does installation take?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  Installation time depends on the coverage package. A full front package typically takes 1–2 days, while a full body wrap requires 3–5 days. We never rush installations — each panel is carefully applied, inspected, and given proper curing time. We'll provide you with an accurate timeline during your consultation based on your specific vehicle and package.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ceramic-combo" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  Can PPF be applied over ceramic coating?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  We recommend applying PPF first, then adding ceramic coating on top. Ceramic coating applied over PPF enhances the film's hydrophobic properties and makes maintenance even easier. Applying PPF over an existing ceramic coating can reduce adhesion. If your vehicle already has ceramic coating, we'll assess the surface and may need to prep it before PPF application.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Protect Your Investment Today
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Schedule a consultation to discuss the best protection package for your vehicle
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Paint Protection Film",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6960 W Russell Rd B",
              "addressLocality": "Las Vegas",
              "addressRegion": "NV",
              "postalCode": "89113",
              "addressCountry": "US"
            }
          },
          "areaServed": {
            "@type": "City",
            "name": "Las Vegas"
          },
          "description": "Professional XPEL paint protection film installation providing self-healing technology, rock chip protection, and long-term paint preservation."
        })}
      </script>
    </div>
  );
};

export default PaintProtectionFilm;
